import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FadeIn, Section, SectionTitle, Accent, Stars, pad2 } from './Shared';
import { OfferCard } from './CoreSections';
import { useCountdown } from '../hooks/useCountdown';
import { coachTestimonials } from '../data/testimonials';
import { faqs } from '../data/faqs';
import { ShieldCheck, Plus, Clock, Lock, CreditCard, ArrowRightIcon, MailIcon, AtSignIcon, MapPinIcon } from 'lucide-react';
export const Testimonials2 = () => <Section className="bg-slate-950" data-id="element-176" data-mp="136z3mjiehzvz">
    <FadeIn data-id="element-177" data-mp="13a4qrt7gyrp2">
      <SectionTitle sub={<span className="inline-flex flex-wrap items-center justify-center gap-2" data-id="element-179" data-mp="phf2tj8drcpb">
            <Stars size={14} data-id="element-180" data-mp="thp8svw6a346" />
            <span className="font-semibold text-white" data-id="element-181" data-mp="tgwamjba4lxn">4,9/5</span>
            <span data-id="element-182" data-mp="1h79tpkbvzag3">· +150 coachs en France</span>
          </span>} data-id="element-178" data-mp="pglvag1lkj64">
        Ce qu’en disent <Accent data-id="element-183" data-mp="1lz1ok722s2c1">les coachs</Accent>
      </SectionTitle>
    </FadeIn>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" data-id="element-184" data-mp="1m0crk4np5623">
      {coachTestimonials.map((t, i) => <FadeIn key={t.name} delay={Math.min(i * 0.05, 0.15)} data-id="element-185" data-mp="2wnunpivjttxq" data-mpi={i}>
          <figure className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-id="element-186" data-mp="2wpe4dal4a701">
            <Stars data-id="element-187" data-mp="3bek36xg1lv7e" />
            <blockquote className="mt-3 text-[15px] leading-relaxed text-slate-200" data-id="element-188" data-mp="36lqyg9kkqmvb">«&nbsp;{t.text}&nbsp;»</blockquote>
            <figcaption className="mt-auto flex items-center gap-3 pt-5" data-id="element-189" data-mp="3kcm7blv2p3yw">
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.bg} text-xs font-bold text-white`} data-id="element-190" data-mp="3gbk9n9gt0zz1">
                {t.initials}
              </div>
              <div className="min-w-0" data-id="element-191" data-mp="3u98r07bnxozc">
                <p className="text-sm font-semibold leading-tight text-white" data-id="element-192" data-mp="3pbqfc969gvnn">{t.name}</p>
                <p className="flex items-center gap-1 text-xs text-slate-500" data-id="element-193" data-mp="1zwseios9xem1">
                  <MapPinIcon size={11} aria-hidden="true" data-id="element-194" data-mp="1uy7din576qwj" /> {t.location} · {t.product}
                </p>
              </div>
            </figcaption>
          </figure>
        </FadeIn>)}
    </div>
  </Section>;
export const Guarantee = () => <Section className="bg-slate-900/40" data-id="element-195" data-mp="3uyrnb6foa46">
    <FadeIn className="mx-auto grid max-w-4xl items-center gap-8 rounded-3xl border border-emerald-500/25 bg-emerald-500/[0.05] p-6 md:grid-cols-[auto_1fr] md:gap-12 md:p-12" data-id="element-196" data-mp="3w0f6f4myp2s">
      <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 md:h-36 md:w-36" data-id="element-197" data-mp="cw5bnu8ak326">
        <div className="text-center" data-id="element-198" data-mp="msi2mkcgc2t8">
          <ShieldCheck size={36} className="mx-auto text-emerald-400" aria-hidden="true" data-id="element-199" data-mp="mrgqsdnm3cag" />
          <p className="mt-1 font-display text-2xl font-black italic leading-none text-white md:text-3xl" data-id="element-200" data-mp="rl1f1dzekrwg">7 jours</p>
        </div>
      </div>
      <div className="text-center md:text-left" data-id="element-201" data-mp="vn67wai11ki1">
        <h2 className="font-display text-3xl font-black uppercase italic leading-[1.05] tracking-tight text-white md:text-5xl" data-id="element-202" data-mp="10lhl8v7j6d50">
          Garantie 7 jours — <span className="text-emerald-400" data-id="element-203" data-mp="15h6j8u3wjlz7">sans justification</span>
        </h2>
        <div className="mt-5 space-y-3 text-base leading-relaxed text-slate-300 md:text-lg" data-id="element-204" data-mp="1fld8bxoiirw9">
          <p data-id="element-205" data-mp="1an1znq9yenfm">Vous ouvrez les tomes. Vous les utilisez dans vos cours.</p>
          <p className="text-lg font-semibold leading-snug text-white md:text-xl" data-id="element-206" data-mp="15qbmbapja5xw">
            Si en 7 jours ce n’est pas ce que vous attendiez&nbsp;: remboursé à 100&nbsp;%.
          </p>
          <p data-id="element-207" data-mp="1ye7h29vjlns1">Sans formulaire. Sans complication. Sans détour.</p>
        </div>
        <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-slate-500" data-id="element-208" data-mp="1tf4gb1mli3pv">
          Taux de remboursement&nbsp;: moins de 2&nbsp;%.
        </p>
      </div>
    </FadeIn>
  </Section>;
export const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <Section className="bg-slate-950" data-id="element-209" data-mp="2h4yupnmkc0c8">
      <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16" data-id="element-210" data-mp="2h607pxn52wwl">
        <FadeIn className="text-center lg:text-left" data-id="element-211" data-mp="2cdgr0rt5nlnk">
          <h2 className="font-display text-4xl font-black uppercase italic leading-[1.05] tracking-tight text-white md:text-6xl" data-id="element-212" data-mp="2cbvz79y86gte">
            Vos questions <Accent data-id="element-213" data-mp="21v24k28dcqf9">fréquentes</Accent>
          </h2>
          <p className="mt-4 text-slate-400" data-id="element-214" data-mp="21sfp7gf8l6mh">
            Une autre question&nbsp;? Écrivez-nous à{' '}
            <a href="mailto:soportecentershop95@gmail.com" className="break-all font-medium text-sky-400 underline-offset-4 hover:underline" data-id="element-215" data-mp="1nxdiir2kfd8m">
              soportecentershop95@gmail.com
            </a>
          </p>
        </FadeIn>
        <FadeIn delay={0.05} data-id="element-216" data-mp="2kljl78fxfvk9">
          <div className="divide-y divide-white/10 border-y border-white/10" data-id="element-217" data-mp="2kks0wtrs5k9a">
            {faqs.map((faq, i) => {
            const isOpen = openIndex === i;
            return <div key={faq.q} data-id="element-218" data-mp="2au5rjhanppz1" data-mpi={i}>
                  <h3 data-id="element-219" data-mp="2atdt7ypode52">
                    <button id={`faq-q-${i}`} className="flex w-full items-center justify-between gap-4 rounded-md py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400" onClick={() => setOpenIndex(isOpen ? null : i)} aria-expanded={isOpen} aria-controls={`faq-a-${i}`} data-id="element-220" data-mp="2ylb6zvcoqg8t">
                      <span className="text-base font-semibold text-white md:text-lg" data-id="element-221" data-mp="11lkdei98o9c5">{faq.q}</span>
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-[transform,background-color] duration-200 ease-out ${isOpen ? 'rotate-45 bg-blue-600 text-white' : 'bg-white/5 text-slate-300'}`} aria-hidden="true" data-id="element-222" data-mp="wok7c7ce36l1">
                        <Plus size={16} data-id="element-223" data-mp="iu19883hap8j" />
                      </span>
                    </button>
                  </h3>
                  <AnimatePresence initial={false} data-id="element-224" data-mp="1ub9eswpv2bea">
                    {isOpen && <motion.div id={`faq-a-${i}`} role="region" aria-labelledby={`faq-q-${i}`} initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.22,
                  ease: [0.23, 1, 0.32, 1]
                }} className="overflow-hidden" data-id="element-225" data-mp="1ud404c51o5iw">
                        <p className="pb-5 pr-12 text-[15px] leading-relaxed text-slate-300 md:text-base" data-id="element-226" data-mp="1dows50hm508x">{faq.a}</p>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </FadeIn>
      </div>
    </Section>;
};
export const CostOfNotHaving = () => {
  const costs = [{
    sin: '3 h par semaine à chercher des séances',
    costo: '156 h par an'
  }, {
    sin: 'Des cours sans structure',
    costo: 'Un groupe qui abandonne'
  }, {
    sin: 'Aucune adaptation par niveau',
    costo: 'Le nouvel adhérent ne revient pas'
  }, {
    sin: 'Aucun programme annuel',
    costo: 'Toujours les mêmes circuits'
  }, {
    sin: 'Le dimanche à chercher des idées sur les réseaux',
    costo: 'Le même cycle chaque année'
  }];
  return <Section className="bg-slate-900/40" data-id="element-227" data-mp="346tlpbx98wg5">
      <FadeIn className="mx-auto max-w-3xl" data-id="element-228" data-mp="345sbf9o2cdzh">
        <SectionTitle data-id="element-229" data-mp="2z7z2i8vdl5vi">
          Combien ça vous coûte
          <br data-id="element-230" data-mp="2z7gxonundjbz" /> <Accent data-id="element-231" data-mp="2z7qpmgz960kb">de ne pas l’avoir&nbsp;?</Accent>
        </SectionTitle>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]" data-id="element-232" data-mp="2u9xj1mu1f4ji">
          <table className="w-full text-left" data-id="element-233" data-mp="2ph4g94i4q6a2">
            <thead data-id="element-234" data-mp="2pg2vg8bba5f5">
              <tr className="border-b border-white/10 bg-white/[0.04]" data-id="element-235" data-mp="2lgc6ma5chzd9">
                <th scope="col" className="w-1/2 px-4 py-3.5 text-xs font-semibold uppercase tracking-wide text-slate-400 md:px-6 md:text-sm" data-id="element-236" data-mp="2lehkwtm92384">
                  Sans le pack
                </th>
                <th scope="col" className="w-1/2 border-l border-white/10 px-4 py-3.5 text-xs font-semibold uppercase tracking-wide text-white md:px-6 md:text-sm" data-id="element-237" data-mp="2bkikbz1tqria">
                  Le vrai coût
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-id="element-238" data-mp="39zpty5eq2dp">
              {costs.map(row => <tr key={row.sin} data-id="element-239" data-mp="3lg48btzv3cz0">
                  <td className="px-4 py-4 text-sm leading-snug text-slate-400 md:px-6 md:text-base" data-id="element-240" data-mp="3let8g59jepd6">{row.sin}</td>
                  <td className="border-l border-white/10 px-4 py-4 text-sm font-medium leading-snug text-red-400 md:px-6 md:text-base" data-id="element-241" data-mp="3qgih5e9aeqy3">
                    <span className="inline-flex items-start gap-1.5" data-id="element-242" data-mp="3ggrnfesjwfz2">
                      <ArrowRightIcon size={14} className="mt-0.5 shrink-0 md:mt-1" aria-hidden="true" data-id="element-243" data-mp="3gi2srj1n9vsa" />
                      {row.costo}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <p className="mt-8 text-center text-lg font-semibold text-white md:text-xl" data-id="element-244" data-mp="2srfgklkp4jzx">
          Votre prochain entraînement peut être différent…
        </p>
      </FadeIn>
    </Section>;
};
const trustItems = [{
  icon: Lock,
  label: 'Paiement 100\u00a0% sécurisé'
}, {
  icon: Clock,
  label: 'Accès en 5 minutes'
}, {
  icon: ShieldCheck,
  label: 'Garantie 7 jours'
}, {
  icon: CreditCard,
  label: 'Carte bancaire · Montant final affiché avant paiement'
}];
export const FinalCTA = () => {
  const {
    hours,
    minutes,
    seconds
  } = useCountdown();
  return <Section className="border-t border-white/5 bg-slate-950 !pb-28 md:!pb-24" data-id="element-245" data-mp="2x2au8tjujgyb">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-246" data-mp="2t17k8nohcf50">
        <FadeIn className="text-center lg:text-left" data-id="element-247" data-mp="2sz4rvwzuu9x4">
          <h2 className="font-display text-[30px] font-black uppercase italic leading-[1.08] tracking-tight text-white md:text-6xl" data-id="element-248" data-mp="36rkoog25xbpl">
            Vous pouvez <Accent data-id="element-249" data-mp="31vm4l4nvafuz">improviser</Accent> votre prochain cours, ou arriver avec tout prêt.
          </h2>
          <p className="mt-4 font-display text-3xl font-black uppercase italic text-sky-400 md:text-4xl" data-id="element-250" data-mp="3gofm0vmp962a">
            À vous de choisir.
          </p>

          <div className="mt-8 inline-flex flex-col items-center gap-3 lg:items-start" data-id="element-251" data-mp="3dysfva4jofis">
            <p className="inline-flex items-center gap-2 rounded-full bg-red-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-red-400 sm:text-xs" data-id="element-252" data-mp="396s9aytyyod8">
              <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-red-400" aria-hidden="true" data-id="element-253" data-mp="306cai2o685ep" />
              Dernière chance · le prix expire dans
            </p>
            <p className="font-display text-5xl font-black tabular-nums leading-none tracking-tight text-white md:text-6xl" role="timer" aria-live="off" data-id="element-254" data-mp="p4idzlzt36c">
              {pad2(hours)}
              <span className="text-slate-600" data-id="element-255" data-mp="3nudcsturaplo">:</span>
              {pad2(minutes)}
              <span className="text-slate-600" data-id="element-256" data-mp="3iw21eeotc71c">:</span>
              {pad2(seconds)}
            </p>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500" data-id="element-257" data-mp="2chj7xnd1mzb4">
              Ensuite, il augmente. Et ne reviendra pas.
            </p>
          </div>

          <ul className="mt-8 hidden flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400 lg:flex" data-id="element-258" data-mp="n0fd87vtnsbw">
            {trustItems.map(({
            icon: Icon,
            label
          }) => <li key={label} className="flex items-center gap-2" data-id="element-259" data-mp="rwcs09rfsqmk">
                <Icon size={14} aria-hidden="true" data-id="element-260" data-mp="vyrc0x6udwff" /> {label}
              </li>)}
          </ul>
        </FadeIn>

        <FadeIn delay={0.05} className="mx-auto w-full max-w-md" data-id="element-261" data-mp="10t5c3cwg8rqf">
          <OfferCard id="final-offer" footnote="Paiement unique · Garantie 7 jours incluse" checklist={['6 Tomes principaux — 365 séances', '5 Bonus stratégiques inclus', 'Versions A/B/C par niveau', 'Accès immédiat et à vie']} data-id="element-262" data-mp="15pc8of2ygqvp" />
        </FadeIn>

        <ul className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-slate-400 lg:hidden" data-id="element-263" data-mp="3hfy3nlwo3t8u">
          {trustItems.map(({
          icon: Icon,
          label
        }, i) => <li key={label} className={`flex items-center gap-1.5 ${i === trustItems.length - 1 ? 'w-full justify-center text-center' : ''}`} data-id="element-264" data-mp="3ljciwacs85l8" data-mpi={i}>
              <Icon size={13} className="shrink-0" aria-hidden="true" data-id="element-265" data-mp="nohvmj8hlz4r" /> {label}
            </li>)}
        </ul>
      </div>
    </Section>;
};
export const Footer = () => <footer className="border-t border-white/5 bg-slate-950 px-5 pb-28 pt-12 md:px-8 md:pb-12" data-id="element-266" data-mp="xm4d9dh684sf">
    <div className="mx-auto flex max-w-6xl flex-col gap-8 md:flex-row md:items-start md:justify-between" data-id="element-267" data-mp="1blnk82y8xa35">
      <div className="max-w-sm self-center text-center md:self-auto md:text-left" data-id="element-268" data-mp="16mu4e3vhl6zt">
        <p className="font-display text-xl font-black uppercase italic tracking-tight text-white" data-id="element-269" data-mp="16kq0vn1i2a02">
          Fitness Fonctionnel <Accent data-id="element-270" data-mp="1gbu6hw5acii8">365™</Accent>
        </p>
        <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-slate-500" data-id="element-271" data-mp="1gcvvz5n1t7jh">
          by Center Shop Digital
        </p>
        <p className="mt-4 text-sm leading-relaxed text-slate-400" data-id="element-272" data-mp="1pd24se7sxjcb">
          Le système n°1 de cours prêts à l’emploi pour les instructeurs et coachs
          de fitness fonctionnel en France. Parce qu’un bon cours ne devrait pas
          vous coûter tous vos dimanches de l’année.
        </p>
      </div>
      <div className="flex flex-col items-center gap-3 text-sm text-slate-400 md:items-end" data-id="element-273" data-mp="1zyflatdoq9jh">
        <a href="mailto:soportecentershop95@gmail.com" className="inline-flex items-center gap-2 transition-colors duration-150 hover:text-white" data-id="element-274" data-mp="23zr003v0tacw">
          <MailIcon size={14} aria-hidden="true" data-id="element-275" data-mp="1q7kncrr2tli7" /> soportecentershop95@gmail.com
        </a>
        <a href="#" className="inline-flex items-center gap-2 transition-colors duration-150 hover:text-white" data-id="element-276" data-mp="1v048qaw09kxk">
          <AtSignIcon size={14} aria-hidden="true" data-id="element-277" data-mp="1gbrd5f0ty43l" /> centershop.digital
        </a>
      </div>
    </div>
    <div className="mx-auto mt-10 flex max-w-6xl flex-col items-center gap-3 border-t border-white/5 pt-6 text-xs text-slate-500 md:flex-row md:justify-between" data-id="element-278" data-mp="1l9st3r2ylnau">
      <p className="text-center md:text-left" data-id="element-279" data-mp="1c93539jxx17o">
        © 2024 Center Shop Digital. Tous droits réservés. Prix affichés en
        euros (EUR).
      </p>
      <p className="flex flex-wrap justify-center gap-4" data-id="element-280" data-mp="149zrehz4go2k">
        <a href="#" className="transition-colors duration-150 hover:text-white" data-id="element-281" data-mp="z9tvuqpqehf7">Conditions générales</a>
        <a href="#" className="transition-colors duration-150 hover:text-white" data-id="element-282" data-mp="uh0umcth1r7v">Politique de confidentialité</a>
      </p>
    </div>
  </footer>;